/**
 * Comprehensive FAQ Data for OctalChip Website
 * 
 * This file contains SEO-optimized FAQs for all page types:
 * - Homepage
 * - Service pages (Web, Mobile, AI, Cloud, Backend, etc.)
 * - Marketing hub + sub-services (WhatsApp, RCS, LinkedIn, email, bulk email/SMS)
 * - Product pages (MailValidator, QuickSite)
 * - Blog pages
 * - Contact/About pages
 * 
 * Each FAQ follows these guidelines:
 * - 40-90 words per answer
 * - Conversational but professional tone
 * - Natural keyword integration
 * - Clear, helpful information
 * - No marketing fluff
 */

export const homepageFAQs = [
  {
    question: "What services does OctalChip offer?",
    answer: "OctalChip provides comprehensive software development services including web development, mobile app development, AI integration, cloud & DevOps, UI/UX design, backend development, and marketing automation (WhatsApp, RCS, email/SMS, LinkedIn outreach, and related channels). We specialize in AI/ML services such as machine learning, deep learning, natural language processing, computer vision, predictive analytics, data science, AI consulting, AI chatbots, reinforcement learning, and workflow automation using n8n, Zapier, and Make. We also ship SaaS products: MailValidator for email validation and QuickSite for done-for-you business websites."
  },
  {
    question: "Does OctalChip ship its own SaaS products?",
    answer: "Yes. MailValidator is our email validation platform with 99.9% accuracy, bulk processing, and a documented API. QuickSite is our done-for-you business website offering with hosting, SSL, multiple templates, and WhatsApp-led domain and go-live support. Both are linked from the homepage SaaS section and the Products page."
  },
  {
    question: "How much does it cost to hire OctalChip developers?",
    answer: "Our pricing starts at $18/hour for hourly engagement, $2560/month for dedicated developers (160 hours), and custom pricing for fixed-cost projects. AI development services are $25/hour with $3200/month for AI/ML dedicated developers (160 hours). Cloud services are $20/hour. We provide detailed quotes based on your project requirements."
  },
  {
    question: "What technologies and frameworks do you specialize in?",
    answer: "We specialize in modern web technologies including React, Next.js, Vue.js, Node.js, Python, TypeScript, and cloud platforms like AWS, Azure, and GCP. For mobile development, we use Flutter and React Native. Our AI/ML expertise includes TensorFlow, PyTorch, OpenAI APIs, and various automation tools like n8n, Zapier, and Make."
  },
  {
    question: "How long does a typical development project take?",
    answer: "Project timelines vary based on complexity. Simple websites take 2-4 weeks, mobile apps take 6-12 weeks, while complex enterprise solutions can take 3-6 months. We provide detailed timelines and milestones during our initial consultation and project planning phase."
  },
  {
    question: "Do you provide ongoing support after project completion?",
    answer: "Yes, we offer comprehensive post-launch support including 3 months of basic maintenance, bug fixes, security updates, and performance monitoring. We also provide extended support packages for ongoing feature enhancements, updates, and optimization."
  },
  {
    question: "Can you work with our existing development team?",
    answer: "Absolutely! We can integrate seamlessly with your existing team, providing additional expertise and resources as needed. We follow your development processes, coding standards, and communication preferences. We can also help train your team on new technologies."
  },
  {
    question: "What is your development methodology?",
    answer: "We follow an agile development methodology with regular sprints, continuous testing, and frequent client communication. Our process includes discovery, design, development, testing, and deployment phases. We use modern tools like Git, CI/CD pipelines, and project management platforms for transparency."
  },
  {
    question: "Do you offer fixed-price or hourly billing options?",
    answer: "We offer both billing models. Hourly engagement ($18/hour) is perfect for flexible projects or MVPs. Fixed-cost projects provide a complete solution within a timeline with no hourly tracking. Monthly dedicated developers ($2560/month) offer 160 hours of dedicated time. For AI/ML projects, monthly dedicated developers are $3200/month (160 hours)."
  }
];

export const webDevelopmentFAQs = [
  {
    question: "What is web development and why do I need it?",
    answer: "Web development involves creating websites and web applications that run in browsers. You need it to establish an online presence, reach customers, automate business processes, or build SaaS platforms. Modern web development ensures your site is fast, secure, mobile-friendly, and optimized for search engines."
  },
  {
    question: "How much does web development cost?",
    answer: "Web development costs vary based on complexity. Simple websites start around $1,000-$5,000, while complex web applications can range from $10,000-$100,000+. Our hourly rate is $18/hour. We provide detailed quotes after understanding your requirements, features, and timeline."
  },
  {
    question: "What technologies do you use for web development?",
    answer: "We use modern frameworks like React, Next.js, Vue.js, and Angular for frontend development. For backend, we use Node.js, Python (Django, Flask), and databases like PostgreSQL and MongoDB. We also implement TypeScript for type safety and use cloud platforms like AWS, Azure, and GCP for deployment."
  },
  {
    question: "How long does it take to build a website?",
    answer: "Timeline is defined by project scope and milestones. Simple websites take 2-4 weeks, e-commerce platforms take 6-12 weeks, and complex SaaS applications take 3-6 months. We break projects into phases with regular milestones and deliver working prototypes early for feedback."
  },
  {
    question: "Will my website be mobile-friendly and responsive?",
    answer: "Yes, all our websites are built with a mobile-first approach, ensuring perfect display on smartphones, tablets, and desktops. We use responsive design techniques and test across multiple devices and screen sizes to ensure optimal user experience everywhere."
  },
  {
    question: "Do you provide SEO optimization with web development?",
    answer: "Yes, we implement SEO best practices including semantic HTML, meta tags, structured data (JSON-LD), fast loading times, mobile optimization, and clean URL structures. We also ensure proper heading hierarchy, alt text for images, and XML sitemaps for better search engine visibility."
  },
  {
    question: "Can you integrate third-party services and APIs?",
    answer: "Absolutely! We integrate payment gateways (Stripe, PayPal), CRM systems (Salesforce, HubSpot), email services (SendGrid, Mailchimp), analytics tools, social media APIs, and cloud storage services. We handle authentication, error handling, and data synchronization."
  },
  {
    question: "What kind of support do you provide after launch?",
    answer: "We provide 3 months of basic support including bug fixes, security updates, and performance monitoring. Extended support packages include feature enhancements, content updates, design improvements, and 24/7 monitoring. We also offer maintenance contracts for ongoing updates."
  }
];

export const mobileAppDevelopmentFAQs = [
  {
    question: "Should I build a native app or cross-platform app?",
    answer: "Native apps (iOS/Android) offer best performance and platform-specific features but require separate development. Cross-platform apps (React Native, Flutter) share code between platforms, reducing cost and time while maintaining good performance. We recommend based on your budget, timeline, and feature requirements."
  },
  {
    question: "How much does mobile app development cost?",
    answer: "Mobile app costs range from $10,000 for simple apps to $100,000+ for complex enterprise apps. Our hourly rate is $18/hour. Factors affecting cost include features, platforms (iOS/Android/both), design complexity, third-party integrations, and backend requirements."
  },
  {
    question: "How long does it take to develop a mobile app?",
    answer: "Simple apps take 6-8 weeks, medium complexity apps take 10-16 weeks, and complex apps with advanced features can take 4-6 months. Timeline includes design, development, testing, and app store submission. We provide detailed project timelines during consultation."
  },
  {
    question: "Do you handle app store submission?",
    answer: "Yes, we handle complete app store submission for both Apple App Store and Google Play Store. This includes preparing app listings, screenshots, descriptions, privacy policies, and managing the submission process. We also help with app store optimization (ASO) to improve visibility."
  },
  {
    question: "Can you integrate payment processing in mobile apps?",
    answer: "Yes, we integrate payment gateways like Stripe, PayPal, Apple Pay, and Google Pay. We implement secure payment processing with PCI compliance, handle in-app purchases, subscriptions, and ensure secure transaction processing with proper encryption and tokenization."
  },
  {
    question: "Do mobile apps work offline?",
    answer: "We can build offline functionality using local storage, caching, and sync mechanisms. Apps can store data locally, work without internet, and sync when connection is restored. This is especially useful for productivity apps, note-taking, and data collection tools."
  },
  {
    question: "What about push notifications and real-time features?",
    answer: "We implement push notifications using Firebase Cloud Messaging (FCM) for Android and Apple Push Notification Service (APNs) for iOS. For real-time features, we use WebSockets, Firebase Realtime Database, or similar technologies to enable live updates, chat, and real-time collaboration."
  },
  {
    question: "How do you ensure app security and data privacy?",
    answer: "We implement security best practices including data encryption, secure API communication (HTTPS), biometric authentication, secure storage, and compliance with GDPR and privacy regulations. We conduct security audits and follow OWASP mobile security guidelines."
  }
];

export const aiIntegrationFAQs = [
  {
    question: "What is AI integration and how can it help my business?",
    answer: "AI integration involves adding artificial intelligence capabilities to your existing systems or building new AI-powered solutions. It can automate processes, improve decision-making, enhance customer experiences, and provide insights from data. Common use cases include chatbots, predictive analytics, and process automation."
  },
  {
    question: "How much does AI integration cost?",
    answer: "AI integration costs range from $5,000 for simple chatbot implementations to $50,000+ for complex ML models and custom AI solutions. Our AI development rate is $25/hour. Cost is set by complexity, data requirements, integration scope, and whether you need custom models or existing APIs."
  },
  {
    question: "What AI technologies and frameworks do you use?",
    answer: "We use Python with TensorFlow, PyTorch, and scikit-learn for machine learning. For NLP, we use OpenAI APIs, Hugging Face transformers, and spaCy. For computer vision, we use OpenCV and YOLO. We also integrate with cloud AI services from AWS, Google Cloud, and Azure."
  },
  {
    question: "Do I need a lot of data for AI integration?",
    answer: "The exact requirement is defined by your use case. Simple integrations using pre-trained models or APIs (like OpenAI) require minimal data. Custom ML models need substantial datasets. We can work with your existing data, help collect more data, or use transfer learning to reduce data requirements."
  },
  {
    question: "How long does AI integration take?",
    answer: "Simple AI integrations (chatbots, API integrations) take 2-4 weeks. Custom ML model development takes 4-10 weeks. Complex AI systems with multiple components can take 3-6 months. Timeline includes data preparation, model development, integration, testing, and deployment."
  },
  {
    question: "Can AI integrate with my existing systems?",
    answer: "Yes, we integrate AI with existing systems through REST APIs, webhooks, SDKs, or direct database connections. We ensure seamless integration with your CRM, ERP, databases, and other business systems while maintaining security and data privacy standards."
  },
  {
    question: "What about AI model accuracy and performance?",
    answer: "We develop models with high accuracy through proper data preparation, feature engineering, and model selection. We test models thoroughly and provide accuracy metrics. For production, we implement monitoring, retraining pipelines, and performance optimization to maintain accuracy over time."
  },
  {
    question: "Do you provide ongoing AI model maintenance?",
    answer: "Yes, we provide ongoing maintenance including model performance monitoring, data drift detection, model retraining, and updates. AI models need periodic retraining as data patterns change. We offer maintenance packages to ensure your AI solutions continue performing optimally."
  }
];

export const cloudDevOpsFAQs = [
  {
    question: "What is cloud migration and why should I migrate?",
    answer: "Cloud migration involves moving applications, data, and infrastructure from on-premises servers to cloud platforms like AWS, Azure, or GCP. Benefits include scalability, cost savings, improved security, automatic backups, and global accessibility. We assess your current setup and create a migration plan."
  },
  {
    question: "How much does cloud migration cost?",
    answer: "Cloud migration costs are based on infrastructure size and complexity. Simple migrations start around $5,000, while enterprise migrations can cost $50,000+. Our cloud services rate is $20/hour. Ongoing cloud costs are usage-based, and cloud reduces infrastructure expenses by 30-50%."
  },
  {
    question: "Which cloud platform should I choose: AWS, Azure, or GCP?",
    answer: "AWS is most popular with extensive services. Azure integrates well with Microsoft ecosystems. GCP excels in data analytics and ML. We recommend based on your existing infrastructure, requirements, team expertise, and budget. We can also implement multi-cloud strategies."
  },
  {
    question: "What is DevOps and how does it help development?",
    answer: "DevOps combines development and operations to automate software delivery. It includes CI/CD pipelines, infrastructure as code, automated testing, and monitoring. Benefits include faster deployments, fewer errors, better collaboration, and continuous improvement. We implement DevOps practices using tools like Docker, Kubernetes, and Jenkins."
  },
  {
    question: "How long does cloud migration take?",
    answer: "Simple migrations take 2-4 weeks, medium complexity takes 4-8 weeks, and enterprise migrations can take 3-6 months. Timeline includes assessment, planning, migration, testing, and optimization. We minimize downtime with phased migrations and rollback plans."
  },
  {
    question: "What about security and compliance in the cloud?",
    answer: "We implement cloud security best practices including encryption, identity access management (IAM), network security groups, DDoS protection, and compliance configurations. We ensure your cloud setup meets industry standards like GDPR, HIPAA, or SOC 2 based on your requirements."
  },
  {
    question: "Do you provide 24/7 monitoring and support?",
    answer: "Yes, we set up comprehensive monitoring using CloudWatch, Azure Monitor, or Google Cloud Monitoring. We configure alerts for performance issues, security events, and downtime. We offer 24/7 monitoring packages with automated responses and on-call support for critical issues."
  },
  {
    question: "Can you help optimize cloud costs?",
    answer: "Absolutely. We analyze your cloud usage, identify cost optimization opportunities, implement auto-scaling, use reserved instances, and optimize resource allocation. We reduce cloud costs by 20-40% through right-sizing, scheduling, and efficient architecture design."
  }
];

export const backendDevelopmentFAQs = [
  {
    question: "What is backend development and why is it important?",
    answer: "Backend development involves server-side logic, databases, APIs, and business logic that power web and mobile applications. It handles data processing, user authentication, business rules, and integrations. A robust backend ensures scalability, security, and performance for your applications."
  },
  {
    question: "How much does backend development cost?",
    answer: "Backend development costs range from $5,000 for simple APIs to $50,000+ for complex microservices architectures. Our hourly rate is $18/hour. Cost is based on API complexity, database design, integrations, security requirements, and scalability needs."
  },
  {
    question: "What backend technologies do you use?",
    answer: "We use Node.js with Express, Python with Django/Flask, and Java/Spring Boot. For databases, we use PostgreSQL, MongoDB, Redis, and MySQL. We also implement GraphQL APIs, RESTful APIs, WebSocket for real-time features, and microservices architectures with Docker and Kubernetes."
  },
  {
    question: "How do you ensure backend security?",
    answer: "We implement security best practices including JWT authentication, OAuth2, role-based access control, data encryption, API rate limiting, input validation, SQL injection prevention, XSS protection, and HTTPS/TLS. We conduct security audits and follow OWASP guidelines."
  },
  {
    question: "Can you build RESTful and GraphQL APIs?",
    answer: "Yes, we build both RESTful APIs following REST principles and GraphQL APIs for flexible data fetching. REST is better for standard CRUD operations, while GraphQL is ideal when clients need specific data combinations. We choose based on your requirements and use case."
  },
  {
    question: "How do you handle database design and optimization?",
    answer: "We design efficient database schemas, create proper indexes, optimize queries, implement caching strategies, and plan for scalability. We use both relational (PostgreSQL, MySQL) and NoSQL (MongoDB, Redis) databases based on data structure and access patterns."
  },
  {
    question: "What about real-time features and WebSockets?",
    answer: "We implement real-time features using WebSockets, Server-Sent Events (SSE), or Firebase Realtime Database. This enables live chat, notifications, collaborative editing, live updates, and real-time dashboards. We choose the best technology based on your requirements."
  },
  {
    question: "Do you provide API documentation?",
    answer: "Yes, we provide comprehensive API documentation including endpoints, request/response formats, authentication methods, error codes, and code examples. We use tools like Swagger/OpenAPI for interactive documentation that developers can test directly."
  }
];

export const workflowAutomationFAQs = [
  {
    question: "What is workflow automation and how does it work?",
    answer: "Workflow automation connects different applications and automates repetitive tasks using tools like n8n, Zapier, and Make. It eliminates manual work by creating automated workflows that trigger actions based on events. For example, when a form is submitted, it can create a CRM record and send an email automatically."
  },
  {
    question: "What's the difference between n8n, Zapier, and Make?",
    answer: "n8n is self-hosted, offering full control and security for internal tools. Zapier is cloud-based with easy setup and popular SaaS integrations. Make (Integromat) excels at complex workflows with advanced data transformation. We recommend based on your needs: security (n8n), simplicity (Zapier), or complexity (Make)."
  },
  {
    question: "How much does workflow automation cost?",
    answer: "Workflow automation costs range from $1,000 for simple integrations to $10,000+ for complex multi-app workflows. Our hourly rate is $18/hour. Costs depend on workflow complexity, number of integrations, data transformations, and whether you need custom solutions or can use existing tools."
  },
  {
    question: "What applications can you integrate?",
    answer: "We integrate CRMs (Salesforce, HubSpot, Pipedrive), email marketing tools (Mailchimp, SendGrid), databases (PostgreSQL, MongoDB), payment gateways (Stripe, PayPal), AI APIs (OpenAI, Claude), web applications, and custom APIs. We can connect virtually any application with an API."
  },
  {
    question: "How long does it take to set up workflow automation?",
    answer: "Simple workflows take 1-2 days, medium complexity takes 3-5 days, and complex multi-app workflows take 1-2 weeks. Timeline includes understanding your processes, designing workflows, setting up integrations, testing, and deployment."
  },
  {
    question: "Can you automate complex business processes?",
    answer: "Yes, we automate complex processes including multi-step workflows, conditional logic, data transformations, error handling, and scheduled tasks. We can handle processes like lead qualification, order processing, data synchronization, reporting, and notification systems."
  },
  {
    question: "What about error handling and monitoring?",
    answer: "We implement comprehensive error handling with retry logic, fallback actions, and notifications. We set up monitoring to track workflow execution, identify failures, and provide alerts. This ensures your automations run reliably and you're notified of any issues."
  },
  {
    question: "Do you provide training on managing workflows?",
    answer: "Yes, we provide training on using n8n, Zapier, or Make, including creating new workflows, modifying existing ones, troubleshooting, and best practices. We also provide documentation and ongoing support to help your team manage workflows independently."
  }
];

export const n8nAutomationFAQs = [
  {
    question: "What is n8n and why should I use it?",
    answer: "n8n is a self-hosted workflow automation tool that gives you full control over your automations. It's open-source, offers enterprise security, and can be deployed on your own infrastructure. It's ideal for internal tools, backend systems, and when you need complete data control."
  },
  {
    question: "How much does n8n automation cost?",
    answer: "n8n itself is open-source and free. Our n8n automation services cost $18/hour. Setup and configuration costs $1,000-$5,000 based on complexity. You'll need server infrastructure, which we can set up on AWS, Azure, or your own servers."
  },
  {
    question: "What can I automate with n8n?",
    answer: "You can automate data pipelines, API integrations, webhook processing, scheduled tasks, database operations, email workflows, and custom business logic. n8n supports 400+ integrations and can connect to any API, making it versatile for various automation needs."
  },
  {
    question: "Do I need technical knowledge to use n8n?",
    answer: "Basic workflows can be created with minimal technical knowledge using n8n's visual interface. Complex workflows with custom code, data transformations, and advanced logic benefit from technical expertise. We provide training and can build workflows for you."
  },
  {
    question: "How secure is n8n for enterprise use?",
    answer: "n8n is highly secure when properly configured. Since it's self-hosted, you control all data and access. We implement authentication, encryption, network security, and access controls. It's suitable for enterprise use with sensitive data and compliance requirements."
  },
  {
    question: "Can n8n integrate with our existing systems?",
    answer: "Yes, n8n can integrate with virtually any system that has an API. We've integrated it with CRMs, databases, email services, payment gateways, cloud storage, and custom applications. We can also create custom nodes for specific integrations if needed."
  },
  {
    question: "What's the difference between n8n cloud and self-hosted?",
    answer: "n8n cloud is hosted by n8n with managed infrastructure. Self-hosted n8n runs on your servers, giving you complete control, security, and no usage limits. We recommend self-hosted for enterprise needs and cloud for simpler use cases."
  },
  {
    question: "Do you provide n8n hosting and maintenance?",
    answer: "Yes, we can set up n8n on cloud platforms (AWS, Azure, GCP) or your infrastructure. We provide ongoing maintenance including updates, monitoring, backup management, and performance optimization. We also offer managed n8n hosting services."
  }
];

export const zapierIntegrationFAQs = [
  {
    question: "What is Zapier and how does it work?",
    answer: "Zapier is a cloud-based automation platform that connects apps without coding. It uses 'Zaps' (automated workflows) that trigger actions when events occur. For example, when a new lead is added to a form, Zapier can create a CRM record and send a notification email automatically."
  },
  {
    question: "How much does Zapier integration cost?",
    answer: "Zapier has free and paid plans ($20-$50/month). Our Zapier integration services cost $18/hour. Setup costs $500-$3,000 based on workflow complexity. We help you choose the right Zapier plan and build your automations."
  },
  {
    question: "What apps can Zapier connect?",
    answer: "Zapier connects 6,000+ apps including Gmail, Slack, Salesforce, HubSpot, Mailchimp, Google Sheets, Trello, Asana, Stripe, PayPal, and thousands more. If an app has an API, Zapier can likely connect it. We can verify compatibility for your specific apps."
  },
  {
    question: "How long does it take to set up Zapier workflows?",
    answer: "Simple Zaps take 30 minutes to 2 hours. Medium complexity workflows take 2-4 hours. Complex multi-step workflows with data transformation take 4-8 hours. We can set up multiple workflows in parallel to speed up the process."
  },
  {
    question: "Can Zapier handle complex business logic?",
    answer: "Yes, Zapier supports multi-step workflows, conditional logic (filters), data transformation (formatters), and error handling. For very complex logic, we can use Zapier's Code by Zapier feature or webhooks to connect to custom backend services."
  },
  {
    question: "What about Zapier task limits and pricing?",
    answer: "Zapier plans have monthly task limits (100 tasks free, 750+ on paid plans). Tasks are individual workflow executions. We help optimize workflows to stay within limits and recommend the right plan. For high-volume needs, we can use webhooks or custom solutions."
  },
  {
    question: "Is Zapier secure and reliable?",
    answer: "Yes, Zapier is SOC 2 compliant, uses encryption, and has 99.9% uptime. It's trusted by millions of users. We implement best practices including secure authentication, error handling, and monitoring to ensure reliable automation."
  },
  {
    question: "Do you provide Zapier training and support?",
    answer: "Yes, we provide training on creating and managing Zaps, troubleshooting, and best practices. We also offer ongoing support for modifying workflows, adding new automations, and optimizing existing Zaps. We create documentation for your team."
  }
];

export const makeAutomationFAQs = [
  {
    question: "What is Make (formerly Integromat) and how is it different?",
    answer: "Make is a visual automation platform that excels at complex workflows with advanced data transformation. Unlike simpler tools, Make offers routers, filters, data transformation modules, and can handle high-volume automation. It's ideal for complex business processes requiring sophisticated logic."
  },
  {
    question: "How much does Make automation cost?",
    answer: "Make has free and paid plans ($9-$299/month based on operations). Our Make automation services cost $18/hour. Setup costs $1,000-$5,000 based on scenario complexity. We help choose the right plan and optimize operations usage."
  },
  {
    question: "What makes Make better for complex workflows?",
    answer: "Make offers advanced features like routers (branching logic), filters, data transformation, array operations, and custom JavaScript. It can handle complex data structures, conditional paths, and high-volume processing. It's more powerful than basic automation tools."
  },
  {
    question: "What applications can Make integrate?",
    answer: "Make integrates with 1,000+ apps including CRMs, databases, email services, payment gateways, cloud storage, social media, and custom APIs. It supports webhooks, HTTP requests, and can connect to virtually any service with an API."
  },
  {
    question: "How long does it take to build Make scenarios?",
    answer: "Simple scenarios take 2-4 hours, medium complexity takes 4-8 hours, and complex multi-path scenarios with data transformation take 1-2 days. We build scenarios efficiently using Make's visual interface and optimize for performance."
  },
  {
    question: "Can Make handle high-volume automation?",
    answer: "Yes, Make is designed for high-volume automation with efficient execution, batching, and operation optimization. Higher-tier plans support millions of operations per month. We optimize scenarios to minimize operations and costs while maintaining performance."
  },
  {
    question: "What about data transformation in Make?",
    answer: "Make excels at data transformation with built-in functions, array operations, data mapping, and custom JavaScript modules. We can transform data formats, combine multiple data sources, filter and sort data, and prepare data for different systems."
  },
  {
    question: "Do you provide Make scenario maintenance?",
    answer: "Yes, we provide ongoing maintenance including scenario updates, error handling improvements, performance optimization, and adding new features. We also monitor scenario execution and provide support for troubleshooting and modifications."
  }
];

export const machineLearningFAQs = [
  {
    question: "What is machine learning and how can it help my business?",
    answer: "Machine learning uses algorithms to learn patterns from data and make predictions or classifications. It can help with sales forecasting, customer segmentation, fraud detection, recommendation systems, and predictive maintenance. We build custom ML models tailored to your specific business needs."
  },
  {
    question: "How much does machine learning development cost?",
    answer: "ML development costs range from $10,000 for simple models to $100,000+ for complex deep learning systems. Our ML development rate is $25/hour. Cost is based on data requirements, model complexity, training time, and deployment infrastructure needs."
  },
  {
    question: "What machine learning frameworks do you use?",
    answer: "We use Python with TensorFlow, PyTorch, scikit-learn, XGBoost, and MLflow. For specific tasks, we use specialized libraries like Pandas for data processing, NumPy for numerical computing, and Hugging Face for pre-trained models. We choose frameworks based on your requirements."
  },
  {
    question: "How much data do I need for machine learning?",
    answer: "Data requirements are defined by model scope. Simple models can use hundreds of examples, while complex models use thousands or millions. We can work with your existing data, help collect more data, use data augmentation techniques, or leverage transfer learning to reduce requirements."
  },
  {
    question: "How long does ML model development take?",
    answer: "Simple ML models take 2-4 weeks, medium complexity takes 4-8 weeks, and complex deep learning models take 2-4 months. Timeline includes data preparation, feature engineering, model training, evaluation, optimization, and deployment."
  },
  {
    question: "What is MLOps and do you provide it?",
    answer: "MLOps (ML Operations) involves deploying, monitoring, and maintaining ML models in production. We provide MLOps including model versioning, automated retraining pipelines, performance monitoring, A/B testing, and deployment automation using tools like MLflow and cloud ML services."
  },
  {
    question: "How accurate will my ML model be?",
    answer: "Model accuracy is driven by data quality, problem complexity, and algorithm selection. We target high accuracy through proper data preparation, feature engineering, and model selection. We provide accuracy metrics, confusion matrices, and continuously improve accuracy through iteration."
  },
  {
    question: "Do ML models need ongoing maintenance?",
    answer: "Yes, ML models need maintenance as data patterns change over time. We provide monitoring, retraining pipelines, performance tracking, and model updates. Models need retraining every few months to maintain accuracy as business conditions evolve."
  }
];

/** FAQs for the /ai-ml-services hub (covers ML, NLP, CV, and related offerings). */
export const aimlServicesFAQs = [
  {
    question: "What AI and machine learning services does OctalChip offer?",
    answer:
      "We provide end-to-end AI and ML services: custom machine learning, natural language processing, computer vision, predictive analytics, conversational AI and chatbots, deep learning, reinforcement learning, data science, and AI consulting, from discovery and data readiness through model development, integration, production deployment, and MLOps.",
  },
  ...machineLearningFAQs.slice(1),
];

export const naturalLanguageProcessingFAQs = [
  {
    question: "What is natural language processing (NLP) and what can it do?",
    answer: "NLP enables computers to understand, interpret, and generate human language. Applications include sentiment analysis, text classification, language translation, chatbots, named entity recognition, and text summarization. We build NLP solutions using modern transformer models and APIs."
  },
  {
    question: "How much does NLP development cost?",
    answer: "NLP costs range from $5,000 for simple text classification to $50,000+ for custom language models. Our NLP development rate is $25/hour. Cost is based on complexity, whether you use pre-trained models or need custom training, and integration requirements."
  },
  {
    question: "What NLP technologies do you use?",
    answer: "We use transformer models (BERT, GPT), Hugging Face libraries, OpenAI APIs, spaCy, NLTK, and TensorFlow/PyTorch. For specific tasks, we use specialized models like sentiment analysis models, translation APIs, and custom fine-tuned models based on your data."
  },
  {
    question: "Can you build chatbots with NLP?",
    answer: "Yes, we build intelligent chatbots using NLP for understanding user intent, context, and generating responses. We use platforms like Dialogflow, Rasa, or custom solutions with OpenAI GPT. Chatbots can handle customer support, sales, FAQs, and complex conversations."
  },
  {
    question: "How accurate are NLP models?",
    answer: "NLP accuracy is driven by the task and model. Pre-trained models like GPT-4 achieve high accuracy for general tasks. Custom models fine-tuned on your data can achieve 90%+ accuracy for specific tasks. We provide accuracy metrics and continuously improve performance."
  },
  {
    question: "What languages do your NLP solutions support?",
    answer: "We can build NLP solutions for multiple languages. Many pre-trained models support 100+ languages. For custom models, we can train on data in your target language. We've worked with English, Spanish, French, German, Hindi, and other languages."
  },
  {
    question: "How long does NLP development take?",
    answer: "Simple NLP tasks (sentiment analysis, text classification) take 2-4 weeks. Custom chatbots take 4-8 weeks. Complex custom language models take 2-4 months. Timeline includes data preparation, model training, integration, testing, and deployment."
  },
  {
    question: "Do you provide ongoing NLP model maintenance?",
    answer: "Yes, we provide maintenance including model performance monitoring, retraining on new data, accuracy improvements, and updates. NLP models benefit from continuous learning as language usage evolves. We offer maintenance packages for ongoing optimization."
  }
];

export const computerVisionFAQs = [
  {
    question: "What is computer vision and what can it do?",
    answer: "Computer vision enables machines to interpret and understand visual information from images and videos. Applications include object detection, image classification, face recognition, OCR (text extraction), quality control, and content moderation. We build CV solutions for various industries."
  },
  {
    question: "How much does computer vision development cost?",
    answer: "CV development costs range from $10,000 for simple image classification to $100,000+ for complex real-time systems. Our CV development rate is $25/hour. Cost is based on complexity, real-time requirements, accuracy needs, and deployment infrastructure."
  },
  {
    question: "What computer vision technologies do you use?",
    answer: "We use OpenCV, YOLO for object detection, ResNet and CNN architectures, TensorFlow, PyTorch, and cloud vision APIs (Google Cloud Vision, AWS Rekognition). We choose technologies based on accuracy requirements, speed, and deployment constraints."
  },
  {
    question: "What industries use computer vision?",
    answer: "Computer vision is used in manufacturing (quality control), healthcare (medical imaging), retail (inventory, analytics), security (surveillance), automotive (autonomous vehicles), agriculture (crop monitoring), and many others. We build solutions tailored to your industry needs."
  },
  {
    question: "How accurate are computer vision models?",
    answer: "CV model accuracy is driven by task setup and data quality. Well-trained models can achieve 95%+ accuracy for classification tasks. Object detection accuracy is measured by mAP (mean Average Precision). We provide accuracy metrics and continuously improve performance."
  },
  {
    question: "Can computer vision work in real-time?",
    answer: "Yes, we build real-time CV systems using optimized models, edge computing, and efficient architectures. Real-time performance is planned around model complexity, hardware, and processing requirements. We optimize for speed while maintaining accuracy."
  },
  {
    question: "How long does computer vision development take?",
    answer: "Simple CV tasks take 3-4 weeks, medium complexity (object detection) takes 4-8 weeks, and complex systems (real-time, multiple objects) take 2-4 months. Timeline includes data collection, annotation, model training, optimization, and deployment."
  },
  {
    question: "Do you handle image data collection and annotation?",
    answer: "Yes, we help collect image datasets, perform data augmentation, and handle annotation for training. We use annotation tools and can work with your existing data. Proper annotation is crucial for model accuracy, and we ensure high-quality labeled datasets."
  }
];

export const predictiveAnalyticsFAQs = [
  {
    question: "What is predictive analytics and how does it work?",
    answer: "Predictive analytics uses historical data and statistical models to forecast future outcomes. It helps with sales forecasting, demand prediction, risk assessment, customer churn prediction, and operational optimization. We build predictive models using machine learning and statistical methods."
  },
  {
    question: "How much does predictive analytics cost?",
    answer: "Predictive analytics costs range from $5,000 for simple forecasting to $50,000+ for complex multi-variable models. Our rate is $25/hour. Cost is based on data complexity, model sophistication, integration requirements, and whether you need real-time predictions."
  },
  {
    question: "What predictive analytics tools do you use?",
    answer: "We use Python with Pandas, NumPy, scikit-learn, Prophet for time series, ARIMA models, XGBoost, and statistical libraries. We also use visualization tools and create dashboards for presenting predictions and insights to stakeholders."
  },
  {
    question: "What can I predict with predictive analytics?",
    answer: "You can predict sales, demand, customer behavior, equipment failures, market trends, risk factors, and operational metrics. Common use cases include sales forecasting, inventory optimization, churn prediction, and maintenance scheduling."
  },
  {
    question: "How accurate are predictive models?",
    answer: "Accuracy is driven by data quality, model selection, and problem complexity. Well-built models achieve 80-95% accuracy for forecasting tasks. We provide accuracy metrics, confidence intervals, and continuously improve predictions."
  },
  {
    question: "How long does predictive analytics development take?",
    answer: "Simple forecasting models take 2-3 weeks, medium complexity takes 3-6 weeks, and complex multi-variable models take 2-3 months. Timeline includes data analysis, feature engineering, model development, validation, and dashboard creation."
  },
  {
    question: "Do I need historical data for predictive analytics?",
    answer: "Yes, predictive models need historical data to learn patterns. More data improves accuracy. We can work with your existing data, help identify data gaps, and use techniques like time series analysis even with limited historical data."
  },
  {
    question: "Can predictive models be updated with new data?",
    answer: "Yes, we build models that can be retrained with new data to maintain accuracy. We implement automated retraining pipelines and monitoring to ensure predictions stay accurate as conditions change. Regular updates improve model performance over time."
  }
];

export const uiUxDesignFAQs = [
  {
    question: "What is UI/UX design and why is it important?",
    answer: "UI (User Interface) design focuses on the visual elements users interact with, while UX (User Experience) design focuses on the overall user journey and satisfaction. Good UI/UX design improves usability, increases conversions, reduces user frustration, and creates memorable brand experiences. It's crucial for product success."
  },
  {
    question: "How much does UI/UX design cost?",
    answer: "UI/UX design costs range from $2,000 for simple wireframes to $50,000+ for comprehensive design systems and full product redesigns. Our design rate is $18/hour. Cost is based on project scope, number of screens, complexity, research needs, and whether you need a complete design system."
  },
  {
    question: "What is included in your UI/UX design process?",
    answer: "Our process includes user research, persona development, information architecture, wireframing, prototyping, visual design, design systems, usability testing, and design handoff to developers. We create responsive designs for all devices and provide design specifications, assets, and style guides."
  },
  {
    question: "What design tools do you use?",
    answer: "We use industry-standard tools including Figma for design and prototyping, Adobe XD, Sketch, and InVision. For user research, we use tools like Miro and user testing platforms. We create interactive prototypes and design systems that ensure consistency across your product."
  },
  {
    question: "How long does UI/UX design take?",
    answer: "Simple projects take 2-4 weeks, medium complexity takes 4-8 weeks, and comprehensive design systems take 2-3 months. Timeline includes research, wireframing, design iterations, prototyping, testing, and final deliverables. We work in sprints with regular client feedback."
  },
  {
    question: "Do you conduct user research and testing?",
    answer: "Yes, we conduct user research including interviews, surveys, and usability testing. We create user personas, journey maps, and test designs with real users to ensure usability. Research helps us understand user needs and validate design decisions before development."
  },
  {
    question: "Can you redesign an existing product?",
    answer: "Absolutely! We analyze existing products, identify usability issues, and create improved designs. We maintain brand consistency while enhancing user experience. Our redesign process includes competitive analysis, user feedback review, and iterative improvements to boost conversions and user satisfaction."
  },
  {
    question: "Do you provide design systems and style guides?",
    answer: "Yes, we create comprehensive design systems including component libraries, color palettes, typography, spacing guidelines, and interaction patterns. Design systems ensure consistency, speed up development, and make future updates easier. We provide documentation and assets for your development team."
  }
];

export const aiConsultingFAQs = [
  {
    question: "What is AI consulting and why do I need it?",
    answer: "AI consulting helps businesses identify AI opportunities, develop strategies, and implement AI solutions that drive value. Consultants assess your business needs, recommend AI use cases, create roadmaps, and guide implementation. It's essential for making informed AI investments and avoiding costly mistakes."
  },
  {
    question: "How much does AI consulting cost?",
    answer: "AI consulting costs range from $5,000 for initial assessments to $50,000+ for comprehensive strategy and implementation guidance. Our consulting rate is $25/hour. Cost is based on scope, complexity, whether you need strategy only or implementation support, and project duration."
  },
  {
    question: "What does an AI consulting engagement include?",
    answer: "Our consulting includes business analysis, AI opportunity assessment, ROI evaluation, technology recommendations, implementation roadmaps, vendor selection guidance, change management strategies, and training plans. We provide detailed reports, presentations, and ongoing support throughout your AI journey."
  },
  {
    question: "How do you identify AI opportunities in my business?",
    answer: "We analyze your business processes, data assets, pain points, and competitive landscape. We identify areas where AI can automate tasks, improve decisions, enhance customer experiences, or create new revenue streams. We prioritize opportunities based on feasibility, impact, and ROI potential."
  },
  {
    question: "Do you help with AI implementation after consulting?",
    answer: "Yes, we provide end-to-end services from strategy to implementation. After consulting, we can build AI solutions, integrate with your systems, train your team, and provide ongoing support. We ensure smooth transition from strategy to production-ready AI systems."
  },
  {
    question: "How long does an AI consulting engagement take?",
    answer: "Initial assessments take 1-2 weeks, comprehensive strategy projects take 4-8 weeks, and full implementation guidance takes 2-3 months. Timeline is set by business complexity, scope of analysis, and whether you need quick wins or comprehensive transformation roadmaps."
  },
  {
    question: "What industries do you consult for?",
    answer: "We consult across industries including healthcare, finance, e-commerce, manufacturing, logistics, education, and SaaS. Each industry has unique AI opportunities. We tailor our approach to your specific industry challenges, regulations, and business models."
  },
  {
    question: "Can you help with AI ROI and business case development?",
    answer: "Yes, we help quantify AI benefits including cost savings, revenue increases, efficiency gains, and competitive advantages. We create detailed business cases with ROI projections, implementation costs, and timelines. This helps secure executive buy-in and justify AI investments."
  }
];

export const deepLearningFAQs = [
  {
    question: "What is deep learning and how is it different from machine learning?",
    answer: "Deep learning uses neural networks with multiple layers to learn complex patterns from data. It's a subset of machine learning that excels at tasks like image recognition, natural language processing, and speech recognition. Deep learning models can automatically learn features without manual engineering."
  },
  {
    question: "How much does deep learning development cost?",
    answer: "Deep learning costs range from $15,000 for simple models to $150,000+ for complex systems. Our rate is $25/hour. Cost is set by model complexity, data requirements, training infrastructure needs, and whether you need custom architectures or pre-trained models."
  },
  {
    question: "What deep learning frameworks do you use?",
    answer: "We use TensorFlow, PyTorch, Keras, and specialized frameworks like YOLO for object detection. We also leverage pre-trained models from Hugging Face and TensorFlow Hub. Framework choice is aligned with your use case, performance requirements, and deployment environment."
  },
  {
    question: "What are common deep learning use cases?",
    answer: "Common use cases include image classification, object detection, facial recognition, natural language understanding, speech recognition, recommendation systems, autonomous vehicles, and medical image analysis. Deep learning excels at complex pattern recognition tasks that traditional ML struggles with."
  },
  {
    question: "How much data do I need for deep learning?",
    answer: "Deep learning requires large datasets (thousands to millions of examples). We use transfer learning with pre-trained models to reduce data requirements, and data augmentation to maximize learning from smaller datasets. We assess your data and recommend the strongest approach."
  },
  {
    question: "How long does deep learning model training take?",
    answer: "Training time ranges from hours for simple models to weeks for complex architectures. Factors include model size, dataset size, hardware (GPU/TPU), and hyperparameters. We optimize training with efficient architectures, transfer learning, and cloud GPU resources to reduce time."
  },
  {
    question: "Do you provide GPU infrastructure for training?",
    answer: "Yes, we set up and manage GPU infrastructure using cloud platforms like AWS SageMaker, Google Colab, or Azure ML. We optimize for cost and performance, using spot instances when possible. We also help you choose between cloud and on-premises solutions."
  },
  {
    question: "Can you deploy deep learning models to production?",
    answer: "Yes, we deploy models using containerization (Docker), cloud ML services, edge devices, or mobile apps. We optimize models for inference speed, implement monitoring, and ensure scalability. We handle model versioning, A/B testing, and continuous improvement in production."
  }
];

export const reinforcementLearningFAQs = [
  {
    question: "What is reinforcement learning and what can it do?",
    answer: "Reinforcement learning (RL) trains agents to make decisions by learning from rewards and penalties. It's used for game AI, robotics, autonomous systems, recommendation optimization, resource allocation, and trading algorithms. RL agents learn optimal strategies through trial and error in simulated or real environments."
  },
  {
    question: "How much does reinforcement learning development cost?",
    answer: "RL development costs range from $20,000 for simple agents to $200,000+ for complex systems. Our rate is $25/hour. Cost is based on environment complexity, training time, simulation needs, and whether you need custom RL algorithms or existing frameworks."
  },
  {
    question: "What reinforcement learning frameworks do you use?",
    answer: "We use OpenAI Gym, Stable Baselines3, Ray RLlib, TensorFlow Agents, and PyTorch. For specific domains, we use specialized frameworks like Unity ML-Agents for game AI. We choose frameworks based on your use case and performance requirements."
  },
  {
    question: "What are common reinforcement learning applications?",
    answer: "Common applications include game AI (chess, Go, video games), robotics control, autonomous vehicle navigation, recommendation system optimization, algorithmic trading, resource scheduling, and adaptive control systems. RL excels when you need agents to learn optimal strategies in dynamic environments."
  },
  {
    question: "How long does RL agent training take?",
    answer: "Training time ranges from days for simple environments to months for complex systems. Factors include environment complexity, reward structure, algorithm choice, and computational resources. We use simulation environments to accelerate training and reduce real-world trial costs."
  },
  {
    question: "Do I need a simulation environment for RL?",
    answer: "Simulations are highly recommended for RL as they allow safe, fast training without real-world risks or costs. We create or use existing simulation environments that closely match your real-world scenario. This enables efficient training before deploying to production."
  },
  {
    question: "Can RL agents adapt to changing conditions?",
    answer: "Yes, RL agents can adapt to changing environments through continuous learning. We implement online learning, transfer learning, and meta-learning techniques. Agents can update their strategies as conditions change, making RL ideal for dynamic, evolving systems."
  },
  {
    question: "How do you ensure RL agent safety and reliability?",
    answer: "We implement safety constraints, reward shaping, and validation testing. We use simulation extensively before real-world deployment, implement monitoring systems, and design fail-safe mechanisms. For critical applications, we use conservative policies and human oversight during initial deployment."
  }
];

export const dataScienceFAQs = [
  {
    question: "What is data science and how can it help my business?",
    answer: "Data science combines statistics, programming, and domain expertise to extract insights from data. It helps with business intelligence, customer analytics, operational optimization, predictive modeling, and data-driven decision making. We turn your data into actionable insights that drive business growth."
  },
  {
    question: "How much does data science cost?",
    answer: "Data science costs range from $5,000 for basic analysis to $50,000+ for comprehensive analytics platforms. Our rate is $25/hour. Cost is based on data complexity, analysis depth, whether you need one-time reports or ongoing dashboards, and integration requirements."
  },
  {
    question: "What data science tools and technologies do you use?",
    answer: "We use Python (Pandas, NumPy, scikit-learn), R, SQL, and visualization tools like Tableau and Power BI. For big data, we use Spark and cloud analytics platforms. We create interactive dashboards, automated reports, and predictive models tailored to your needs."
  },
  {
    question: "What types of data analysis do you provide?",
    answer: "We provide descriptive analytics (what happened), diagnostic analytics (why it happened), predictive analytics (what will happen), and prescriptive analytics (what should be done). We analyze sales, customer behavior, operations, financials, and any business metrics you need insights on."
  },
  {
    question: "How do you handle data quality and cleaning?",
    answer: "We assess data quality, identify missing values, outliers, and inconsistencies. We clean data, handle duplicates, normalize formats, and create data pipelines. We document data quality issues and work with you to improve data collection processes for better future analysis."
  },
  {
    question: "Do you create dashboards and visualizations?",
    answer: "Yes, we create interactive dashboards using Tableau, Power BI, or custom web dashboards. Dashboards provide real-time insights, KPIs, trends, and drill-down capabilities. We design user-friendly interfaces that make complex data accessible to business users and executives."
  },
  {
    question: "How long does a data science project take?",
    answer: "Simple analysis projects take 2-4 weeks, comprehensive analytics take 6-12 weeks, and full BI platform implementations take 2-4 months. Timeline includes data collection, cleaning, analysis, model development, dashboard creation, and documentation."
  },
  {
    question: "Can you help with ongoing data analysis and reporting?",
    answer: "Yes, we provide ongoing data analysis services including automated reporting, dashboard maintenance, ad-hoc analysis, and regular insights. We can set up automated pipelines that deliver reports on schedule and alert you to important trends or anomalies in your data."
  }
];

export const aiChatbotsFAQs = [
  {
    question: "What are AI chatbots and how do they work?",
    answer: "AI chatbots are conversational agents that understand natural language and provide intelligent responses. They use NLP and machine learning to understand context, maintain conversations, and answer questions. They can handle customer support, sales, FAQs, and complex interactions 24/7."
  },
  {
    question: "How much does chatbot development cost?",
    answer: "Chatbot costs range from $3,000 for simple FAQ bots to $30,000+ for advanced conversational AI. Our chatbot development rate is $25/hour. Cost is based on complexity, integration requirements, training data, and whether you use platforms or custom development."
  },
  {
    question: "What chatbot platforms do you use?",
    answer: "We use OpenAI GPT for advanced conversations, Dialogflow for Google integrations, Rasa for custom solutions, Microsoft Bot Framework, and custom NLP models. We choose platforms based on your needs, integration requirements, and conversation complexity."
  },
  {
    question: "Can chatbots integrate with our systems?",
    answer: "Yes, chatbots can integrate with CRMs, databases, payment systems, email services, and APIs. They can retrieve information, create records, process transactions, and trigger workflows. We ensure seamless integration with your existing business systems."
  },
  {
    question: "How long does chatbot development take?",
    answer: "Simple FAQ chatbots take 1-2 weeks, conversational bots take 3-6 weeks, and advanced AI chatbots with complex logic take 2-3 months. Timeline includes design, training, integration, testing, and deployment."
  },
  {
    question: "Do chatbots support multiple languages?",
    answer: "Yes, modern chatbots support multiple languages. Platforms like Dialogflow support 20+ languages, and custom solutions can be trained for specific languages. We can build multilingual chatbots that detect and respond in the user's language."
  },
  {
    question: "How do you train chatbots?",
    answer: "We train chatbots using conversation data, FAQs, knowledge bases, and user interactions. For AI chatbots, we fine-tune models on your data. We also implement continuous learning so chatbots improve from real conversations over time."
  },
  {
    question: "What about chatbot analytics and monitoring?",
    answer: "We implement analytics to track conversation quality, user satisfaction, common questions, and chatbot performance. We monitor for errors, provide insights, and continuously improve chatbots based on usage data and user feedback."
  }
];

export const mailValidatorFAQs = [
  {
    question: "What is the Email Validator SaaS and how does it work?",
    answer: "Our Email Validator SaaS is a professional email validation service that checks if email addresses are valid, deliverable, and safe to send to. It verifies syntax, domain validity, mailbox existence, and checks for disposable emails and spam traps. This helps reduce bounce rates and improve deliverability."
  },
  {
    question: "How accurate is the email validation service?",
    answer: "Our email validation service has a 99.9% accuracy rate. We use multiple validation techniques including syntax checking, domain verification, SMTP verification, and real-time mailbox checks. This ensures reliable results for your email marketing campaigns and reduces false positives."
  },
  {
    question: "Do you offer bulk email validation?",
    answer: "Yes, we support both single email validation and bulk processing for large email lists. You can upload CSV files or use our API to validate thousands of emails efficiently. Bulk validation helps clean your email lists before campaigns to improve deliverability and sender reputation."
  },
  {
    question: "How many free credits do I get?",
    answer: "We offer 100 free credits with company domain signup, allowing you to test our service before committing to a paid plan. Free credits let you validate 100 email addresses to see the accuracy and features of our service."
  },
  {
    question: "What information does email validation provide?",
    answer: "Our validation provides email validity status, deliverability score, domain information, mailbox status, disposable email detection, role-based email detection (like info@, support@), and spam trap detection. This helps you make informed decisions about your email list."
  },
  {
    question: "Can I integrate email validation via API?",
    answer: "Yes, we provide RESTful APIs for easy integration with your applications, websites, and marketing tools. The API supports single and bulk validation, webhooks for async processing, and returns detailed validation results in JSON format."
  },
  {
    question: "How does email validation improve deliverability?",
    answer: "Email validation removes invalid, non-existent, and risky email addresses from your list. This reduces bounce rates, improves sender reputation, and increases inbox placement. ISPs favor senders with low bounce rates, leading to better deliverability and higher open rates."
  },
  {
    question: "What pricing plans are available?",
    answer: "We offer flexible pricing plans based on validation volume. Plans start with 100 free credits for testing. Paid plans scale based on your needs, with volume discounts for large lists. Contact us for custom enterprise pricing for high-volume requirements."
  }
];

export const quickSiteFAQs = [
  {
    question: "What exactly do I get with QuickSite?",
    answer: "QuickSite delivers a complete business website built from the details you share, plus hosting, SSL, and ongoing developer support after go-live. Domain setup is handled with you on WhatsApp. You can keep your current domain or choose a new one, and we guide you through configuration."
  },
  {
    question: "What does QuickSite cost?",
    answer: "QuickSite is a one-time fee of ₹3,999 for website development and ongoing management, with no monthly website-builder subscription. Domain registration or transfer is handled with you on WhatsApp; whether the domain is included or billed separately depends on the domain you choose."
  },
  {
    question: "Are there any monthly fees for QuickSite?",
    answer: "There is no monthly website-builder subscription. The one-time ₹3,999 fee covers development and ongoing management. Your domain is managed with us on WhatsApp; we confirm whether it is included or billed separately based on the domain you choose before you pay."
  },
  {
    question: "I already have a domain. Can I use it with QuickSite?",
    answer: "Yes. Tell us on WhatsApp and we connect your existing domain to your new site so visitors reach the QuickSite build you approve."
  },
  {
    question: "Will you update my QuickSite after launch?",
    answer: "Yes. You can reach our developers on WhatsApp or email to change content, sections, or design after go-live."
  },
  {
    question: "How long until my QuickSite is ready?",
    answer: "After you pick a template and continue on WhatsApp, we prepare your site and complete domain setup and go-live on a clear launch schedule shared with you upfront."
  },
  {
    question: "How do I start with QuickSite?",
    answer: "Visit https://quicksite.octalchip.com/, enter your business name and description, choose a template, then continue on WhatsApp where we handle domain, payment, and go-live."
  }
];

export const blogFAQs = [
  {
    question: "What topics does the OctalChip blog cover?",
    answer: "Our blog covers web development, mobile app development, AI and machine learning, cloud computing, DevOps, automation tools (n8n, Zapier, Make), software engineering best practices, technology trends, and tutorials. We publish practical guides, case studies, and industry insights."
  },
  {
    question: "How often do you publish new blog posts?",
    answer: "We publish new posts regularly, including technical guides, case studies, and tutorials across web and mobile development, AI, cloud, automation, and related topics. Content emphasizes practical value and strong technical depth. Subscribe to our newsletter or RSS feed to get notified when we publish new articles."
  },
  {
    question: "Can I contribute a guest post to your blog?",
    answer: "We welcome guest contributions from industry experts. Guest posts should provide value, be well-researched, and align with our topics. Contact us with your topic idea and writing samples. We review all submissions and provide feedback."
  },
  {
    question: "Are blog posts optimized for SEO?",
    answer: "Yes, all our blog posts are optimized for SEO including keyword research, meta descriptions, heading structure, internal linking, image optimization, and structured data. We focus on providing value while ensuring search engine visibility."
  },
  {
    question: "Can I share or republish your blog content?",
    answer: "You can share our blog posts with proper attribution and links. For republishing, please contact us for permission. We appreciate shares and backlinks, which help us reach more readers interested in development and technology topics."
  },
  {
    question: "How do I stay updated with new blog posts?",
    answer: "You can subscribe to our newsletter, follow us on social media (LinkedIn, X @octalchip, and other channels linked from the site), or use our RSS feed. We notify subscribers about new posts, industry updates, and exclusive content. You can unsubscribe anytime."
  },
  {
    question: "Do you accept topic suggestions?",
    answer: "Yes, we welcome topic suggestions from our readers. If you have questions or topics you'd like us to cover, contact us or leave a comment. We prioritize topics that help our audience solve real problems and learn new skills."
  },
  {
    question: "Are blog comments moderated?",
    answer: "Yes, we moderate comments to ensure quality discussions and prevent spam. We encourage constructive comments, questions, and feedback. Comments that add value to the discussion are approved and responded to by our team."
  }
];

export const contactFAQs = [
  {
    question: "How can I contact OctalChip?",
    answer: "You can contact us through our contact form for project discussions and support requests. We also offer a free 30-minute consultation at https://cal.com/octalchip/30min. We respond quickly and keep communication clear from the first message."
  },
  {
    question: "What are your business hours?",
    answer: "Core hours are Monday to Friday, 9:00 AM to 6:00 PM IST for consultations and routine project communication. We aim to reply to WhatsApp within about 2 hours on business days. For urgent or critical issues, we arrange extended coverage as agreed in your engagement."
  },
  {
    question: "Do you offer free consultations?",
    answer: "Yes, we offer free initial consultations to discuss your project requirements, understand your needs, and provide recommendations. Book a 30-minute consultation through our calendar link (https://cal.com/octalchip/30min). Consultations help us provide accurate quotes and timelines."
  },
  {
    question: "How quickly do you respond to inquiries?",
    answer: "We aim to respond to all inquiries within 24 hours during business days. For urgent matters, we prioritize responses. You can also book a call directly through our calendar for immediate discussion of your project needs."
  },
  {
    question: "What information should I include in my inquiry?",
    answer: "Include your project type, goals, timeline, budget range, and any specific requirements. The more details you provide, the better we can understand your needs and provide accurate recommendations and quotes."
  },
  {
    question: "Do you work with clients globally?",
    answer: "Yes, we work with clients globally. We've served clients in United States, United Kingdom, Germany, France, Netherlands, Canada, Australia, UAE, Singapore, India, Saudi Arabia, Mexico, Brazil, Japan, South Korea, Poland, Romania, Bulgaria and other countries. We communicate in English, Hindi, and Gujarati, and accommodate different time zones for meetings and collaboration."
  },
  {
    question: "Can I visit your office?",
    answer: "Yes, our office is located at 404 Opera Point, Kotharia, Gujarat, India 360022. Please contact us in advance to schedule a visit. We also conduct virtual meetings via video calls for clients who prefer remote consultations."
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept payments via bank transfer, credit cards, PayPal, and other standard payment methods. Payment terms are discussed during project planning. We provide invoices and can accommodate different payment schedules based on project milestones."
  }
];

export const aboutFAQs = [
  {
    question: "What is OctalChip and what do you do?",
    answer: "OctalChip is a technology company specializing in software development, AI integration, cloud services, marketing automation, and digital transformation. We help businesses build web applications, mobile apps, AI solutions, and automate workflows. We also ship SaaS products: MailValidator for email validation and QuickSite for done-for-you business websites. Our team combines technical expertise with business understanding."
  },
  {
    question: "How long has OctalChip been in business?",
    answer: "OctalChip was founded in 2022 and has 4+ years of experience delivering software development and technology services. We have 25+ agile-enabled developers and have completed 75+ delivered projects across various industries."
  },
  {
    question: "Where is OctalChip located?",
    answer: "OctalChip is based in Gujarat, India, with our office at 404, Opera Point, Kotharia, Gujarat, India 360022. We serve clients globally, working with businesses in India, Europe, Middle East, and other regions. Our remote-first approach allows us to collaborate effectively worldwide."
  },
  {
    question: "What industries do you serve?",
    answer: "We serve various industries including e-commerce, SaaS, healthcare, fintech, manufacturing, real estate, and technology companies. Our solutions are adaptable to different business needs, and we customize our services based on industry-specific requirements."
  },
  {
    question: "What makes OctalChip different?",
    answer: "We combine technical expertise with business understanding, offer competitive pricing ($18/hour), provide comprehensive support, and focus on long-term partnerships. Our agile methodology, modern tech stack, and commitment to quality set us apart in the development services market."
  },
  {
    question: "What is your team size and expertise?",
    answer: "Our team includes 25+ agile-enabled developers, AI/ML engineers, cloud architects, and project managers. Team members have expertise in modern technologies, best practices, and industry standards. We scale teams based on project requirements."
  },
  {
    question: "Do you have case studies or portfolio?",
    answer: "Yes, we have completed 75+ delivered projects across various domains. While we maintain client confidentiality, we can discuss relevant experience during consultations. Our services page and blog showcase our expertise and the types of solutions we build."
  },
  {
    question: "What is your development process?",
    answer: "We follow an agile development process with regular communication, iterative development, and continuous testing. Our process includes discovery, planning, design, development, testing, deployment, and support phases. We use modern tools and methodologies for efficient delivery."
  }
];

export const marketingServicesFAQs = [
  {
    question: "What is marketing automation (and why does it matter)?",
    answer:
      "Marketing automation is running campaigns across WhatsApp, email, RCS, and similar channels with triggers, segments, and real metrics behind them. Blasting everyone the same way is mostly dead; what works now is consent you can point to, fast replies on mobile, and sending that passes authentication and spam checks so you actually land in the inbox.",
  },
  {
    question: "How do you handle WhatsApp automation consent, templates, and pacing?",
    answer:
      "WhatsApp splits things into customer care windows (where you can reply conversationally) and approved templates for outbound or follow-up. Templates go through review, and Meta can throttle you if quality drops. We set up clear opt-in, sensible send limits, and pacing so you are not racing into blocks or template rejections.",
  },
  {
    question: "What does bulk automation marketing include, and how do you protect deliverability?",
    answer:
      "Bulk automation is segmented sends, some personalization, and send rules that keep volume under control instead of one huge blast. On email we align SPF, DKIM, and DMARC and wire one-click unsubscribe where Gmail expects it above high daily marketing volume. On US SMS we register A2P 10DLC so carriers treat your traffic as registered and opted-in, which cuts filtering.",
  },
  {
    question: "How do you run LinkedIn outreach without violating platform rules?",
    answer:
      "We do not use bots, scrapers, or tools that click or message for you. Your team keeps hands on the account; we help with lists, research notes, and draft copy (including AI-assisted lines you edit). That keeps you inside LinkedIn’s rules while still making outreach repeatable.",
  },
  {
    question: "What is RCS messaging (and why rich cards convert better)?",
    answer:
      "RCS for Business adds branded, interactive messages: cards with image and text, suggested replies, and actions. Carousels can show several products or options in one thread. People tap instead of copying a link from a plain text SMS, so more of them complete the step you care about.",
  },
  {
    question: "How do you approach email marketing in a compliance-first way?",
    answer:
      "We start with authentication (SPF/DKIM) and DMARC aligned to your from domain, plus an obvious unsubscribe path. At higher marketing volume, Gmail expects one-click unsubscribe on those messages. List hygiene, quiet hours, and tight segments do more for inbox placement than louder subject lines.",
  },
  {
    question: "Can you integrate marketing channels with our CRM and analytics?",
    answer:
      "Yes. Fires from your CRM or stack (new lead, deal stage, order, ticket closed) can trigger WhatsApp, SMS, email, or RCS where it fits. We hook delivery and clicks back into reporting so you see what actually drove replies and revenue, not just what was sent.",
  },
  {
    question: "How do you measure ROI across multiple channels?",
    answer:
      "We look at what each channel delivered (sends, reads where available, replies, clicks) and tie conversions to a single customer key where we can so nothing gets counted twice. From there you can see which campaigns paid off and which lists or messages to fix.",
  },
];

export const whatsappAutomationFAQs = [
  {
    question: "What is WhatsApp Business automation and when should we use it?",
    answer:
      "It is the official Business Platform (Cloud API is common) sending approved templates when you reach out, and normal chat replies inside the customer care window after someone writes to you. Use it for shipping updates, support, bookings, and marketing only where people clearly opted in. Picking the right template category (utility vs marketing) saves you from rejections and quality hits.",
  },
  {
    question: "What is the difference between session messaging and template messaging?",
    answer:
      "If the customer messages you first, you have a window to reply in freeform without opening a new template for that thread. Once that window closes, or for cold outbound, you need a pre-approved template in the correct category. We wire automations so session replies and templates hand off cleanly and variables pull from your CRM or orders without breaking.",
  },
  {
    question: "How do you handle WhatsApp template approvals and quality ratings?",
    answer:
      "Every template goes to Meta with fixed wording and placeholders. Your send limits and number health can move with user feedback. We keep versions tidy, test in sandbox first, and watch delivery and blocks. If something gets rejected or quality slips, we fix copy and pacing instead of pushing harder on a bad template.",
  },
  {
    question: "How do you capture and prove opt-in for WhatsApp marketing?",
    answer:
      "Opt-in should say what you will send and how to stop, captured on a form, at checkout, via QR, or in your app, with a timestamp stored in CRM or the BSP. We split lists by what they agreed to and where they are. That paper trail helps if anyone audits you and it cuts down reports and blocks. Frequency caps stop people from muting you.",
  },
  {
    question: "Can WhatsApp integrate with our CRM, helpdesk, and ecommerce stack?",
    answer:
      "Yes. Inbound messages, delivery receipts, and template sends can hit webhooks that update CRM, open tickets, or fire on shipped orders. Shipment and payment systems can trigger the right template. We build retries and deduping so a flaky webhook does not double-message someone.",
  },
  {
    question: "What use cases do you automate on WhatsApp beyond broadcasts?",
    answer:
      "Lots of teams start with order and delivery status, then add cart recovery, booking changes, quick replies for FAQs, and handoff to a human with the full thread. OTP as a second channel is common. We lock service and transactional flows first, then add promo only to people who actually opted into marketing.",
  },
  {
    question: "How do you monitor performance and improve WhatsApp campaigns?",
    answer:
      "We watch sent, delivered, read, failed, plus replies and conversions tied to campaigns where we can. If blocks or odd failure rates spike, we dig in early. Small tests on template text and send time beat guessing.",
  },
  {
    question: "What does implementation involve with OctalChip?",
    answer:
      "Number and Business Manager setup, webhooks in place, template set agreed and submitted, then journeys and CRM or shop hooks. We QA on real phones, train whoever runs it day to day, and leave you with simple runbooks for template edits. Timeline is set by how many templates and systems you connect, and whether you are moving from another provider.",
  },
];

export const rcsMessagingFAQs = [
  {
    question: "What is RCS messaging and how is it different from SMS?",
    answer:
      "RCS for Business is richer messaging in the default Android messages app: your brand, read receipts, cards with images, and buttons. Plain SMS is still the widest reach and works everywhere RCS does not. We always plan a short SMS (or MMS) version when RCS is not supported on that phone or network.",
  },
  {
    question: "When should we choose RCS instead of or alongside SMS?",
    answer:
      "RCS helps when you want a product carousel, a few clear actions, or a visual promo. SMS is still best for simple alerts and for anyone who cannot get RCS. A lot of programs hit Android-heavy lists with RCS and keep SMS as the backup, with one suppression list so nobody gets hammered on both.",
  },
  {
    question: "How do rich cards and carousels improve engagement?",
    answer:
      "A card puts the image, short copy, and a button in one place so people tap instead of typing a URL. A carousel lines up several products or locations in one scroll. Suggested replies cut typing. You still stay inside the thread, which feels lighter than jumping to email.",
  },
  {
    question: "How do you handle consent, compliance, and opt-out for RCS?",
    answer:
      "Same mindset as SMS: prove consent where the law asks for it, honor STOP and your own opt-out paths, and share suppressions with email if you are running both. Carriers and platforms have their own rules; we keep content and send categories sensible so you are less likely to get filtered.",
  },
  {
    question: "What reporting can we expect from RCS campaigns?",
    answer:
      "Your provider gives delivery, reads on supported devices, and taps on actions. If you tag links or send server-side conversion events, you can tie that to revenue. Comparing a small holdout or region test to SMS tells you more than open rate alone.",
  },
  {
    question: "How does SMS fallback work in practice?",
    answer:
      "If RCS is not available, the send becomes a normal text (or MMS with a link). We never make a step RCS-only if someone still needs the info. Copy is shorter on SMS, so we keep two versions of the same journey.",
  },
  {
    question: "Can RCS integrate with our CRM and automation stack?",
    answer:
      "Yes. Same idea as SMS APIs: a deal moves stage, an order ships, a webhook fires, and RCS goes out. Replies and button taps can write back to CRM. We sign webhooks, retry on failure, and avoid double sends when the same event fires twice.",
  },
  {
    question: "What is involved in launching RCS with OctalChip?",
    answer:
      "Pick the journeys, build the cards and fallbacks, finish sender branding with your provider, then pilot on a slice of the list while we watch errors and engagement. After that we tune creative rotation and frequency. When offers or rules change, we update templates in a controlled way.",
  },
];

export const linkedInOutreachFAQs = [
  {
    question: "What does compliant LinkedIn outreach mean in practice?",
    answer:
      "Follow LinkedIn’s rules: no bots, no scraping, no app that clicks or messages for you. Your reps stay on the keyboard; we help with lists, research notes, and drafts (AI can suggest lines you still edit). That is slower than a bot farm, but your account stays safer.",
  },
  {
    question: "How is this different from typical LinkedIn automation tools?",
    answer:
      "Most automation tools blast invites and DMs until LinkedIn flags you. We keep sends human-controlled, agree on angles and templates together, and use AI as a writing assist, not an autopilot. You trade raw volume for an account you can still log into next year.",
  },
  {
    question: "How do you use AI without making outreach feel generic?",
    answer:
      "We feed it real inputs (role, industry, one concrete trigger like a funding round or job change) and you fix tone before it goes out. No fake personalization. We rotate hooks, keep a do-not-contact list, and drop anything that reads like spam.",
  },
  {
    question: "Can you align outreach with Sales Navigator and our CRM?",
    answer:
      "Yes. Saved searches and account tiers map to how you log replies, meetings, and nos in CRM. Reps get tasks and reminders from that. Sales and marketing end up looking at the same pipeline story instead of two spreadsheets.",
  },
  {
    question: "What metrics should we track for LinkedIn outbound?",
    answer:
      "Accepts, replies, meetings booked, and pipeline tied to those touches. Watch blocks and reports too. Try one sequence per persona and see what actually books calls, not just what gets the most replies.",
  },
  {
    question: "How do you handle GDPR, cold outreach, and data privacy?",
    answer:
      "For EU-style rules we use tight B2B targeting, say who you are in the message, offer an easy out, and stop anyone who opts out. Store only what you need in CRM or sheets. Your lawyer signs off on the approach per country; we handle retention and access in the tools we set up.",
  },
  {
    question: "What roles does your team play versus our internal reps?",
    answer:
      "We write the playbook, build the lists and integrations, and train your team to run it. Clicks and sends stay on your accounts unless we agree something else in writing. Everyone knows who owns compliance and login access before we start.",
  },
  {
    question: "How long until we see results from a LinkedIn program?",
    answer:
      "You learn what messaging fits in a few weeks. Real pipeline often needs a quarter or two if your deals are large. We adjust weekly, but we care about reply quality and meetings, not spraying the whole market in month one.",
  },
];

export const emailMarketingFAQs = [
  {
    question: "What is email marketing automation and what problems does it solve?",
    answer:
      "Automation fires email when something happens (signup, purchase, quiet for 30 days) so nobody has to remember to click send. Nurture, onboarding, renewals, and win-backs run on a schedule. With decent segments you send less junk and more of what people actually open.",
  },
  {
    question: "How do SPF, DKIM, and DMARC affect deliverability?",
    answer:
      "SPF and DKIM tell Gmail and others that your ESP is allowed to send for your domain and that the message was not tampered with. DMARC ties them together and gives you reports when something fails alignment. We check DNS, subdomains, and ESP settings, then watch postmaster tools and complaints after we change anything.",
  },
  {
    question: "What is one-click unsubscribe and when is it required?",
    answer:
      "One-click means the user can leave your marketing list from the inbox in one step, via List-Unsubscribe headers. Gmail expects it once you are over their bulk marketing volume bar. Done right it cuts spam complaints. Transactional mail (receipts, password resets) stays on separate streams so it does not inherit promo unsubscribes.",
  },
  {
    question: "How do you separate transactional email from promotional email?",
    answer:
      "Different subdomain or pool, templates tagged by purpose, and rules so someone who dropped marketing still gets order and security mail. Mixing those streams is how receipts start landing in spam.",
  },
  {
    question: "Can you migrate us to a new ESP without damaging reputation?",
    answer:
      "Yes. Warm the new domain or pool slowly, move suppressions and bounces over, plan DKIM cuts carefully, and watch placement while volume ramps. We also show your team where not to change tracking domains or headers by accident on go-live night.",
  },
  {
    question: "How do you approach segmentation and lifecycle journeys?",
    answer:
      "Split by what people did, what they bought, and what they agreed to receive. Map welcome, activation, upsell, and churn-risk tracks. Modular HTML speeds builds. Inactive lists get sunset or re-permission, not endless blasts. Test on clicks and revenue, not only open rate.",
  },
  {
    question: "What reporting do you set up for email programs?",
    answer:
      "UTMs that match your naming habit, ESP stats in one dashboard, and conversions from the site or CRM where we can wire them. B2B teams want opportunity influence, not just blast stats. Weekly or monthly we look at bounces, complaints, and whether authentication still lines up.",
  },
  {
    question: "How does OctalChip price and deliver email marketing work?",
    answer:
      "Hourly or fixed projects depending on depth: audit, templates, journeys, integrations, or a light retainer to tune sends. You get docs and handover so your team can run it without calling us for every broadcast.",
  },
];

export const bulkAutomationMarketingFAQs = [
  {
    question: "What is bulk automation marketing for email and SMS?",
    answer:
      "High-volume email and SMS driven by segments and triggers, not one giant blast to everyone. Throttles, suppressions, and consent checks keep carriers and inbox providers from throttling you. Same audience should not get conflicting messages on both channels the same hour unless you meant to.",
  },
  {
    question: "How do you protect email deliverability at high send volumes?",
    answer:
      "SPF, DKIM, DMARC lined up, warm new domains slowly, and send engaged segments before ice-cold buys. Clean bounces, honor unsubscribes, add one-click unsubscribe where Gmail wants it. Seed inboxes and ramp by cohort so a mistake does not hit a million people at once.",
  },
  {
    question: "What is A2P 10DLC and why does it matter for US SMS?",
    answer:
      "US carriers want your brand and each use case registered for long-code business SMS. Skip that and messages get filtered or dropped. We help file brand and campaign paperwork and keep descriptions honest so your traffic keeps moving as you scale.",
  },
  {
    question: "How do you manage consent and opt-out across email and SMS?",
    answer:
      "One suppression file (or synced system) so STOP and unsubscribe both stick where they should. Marketing stops; password reset and order texts still flow if that is how you set it up. Opt-in language lives in one place so nobody gets texts they never asked for.",
  },
  {
    question: "How do frequency caps and quiet hours work in bulk programs?",
    answer:
      "Caps limit touches per day or week across all campaigns. Quiet hours hold sends until local morning unless it is truly urgent (fraud alert, etc.). VIPs and prospects can get different rules. We watch where people still get too many pings and tighten the cap.",
  },
  {
    question: "Can bulk campaigns still be personalized?",
    answer:
      "Yes: merge fields, recent browse or purchase, simple recommendations from CRM or warehouse. SMS stays short and avoids creepy detail. Email can go deeper. A/B one personalized version against a plain one and see if it is worth the build.",
  },
  {
    question: "How do you measure success for high-volume email and SMS?",
    answer:
      "Delivery, failures, clicks, opt-outs, and money per thousand sends. SMS: replies and completed actions. Email: spam placement and inbox rate. Tie conversions to one customer ID when you can so email and SMS do not both claim the same sale.",
  },
  {
    question: "What integrations are common for bulk automation programs?",
    answer:
      "ESP plus SMS provider, Shopify or your cart, Salesforce or HubSpot, sometimes BigQuery or Snowflake for audiences. Webhooks into a queue handle traffic spikes. We leave README-level notes on retries and where to look when a send sticks.",
  },
];

// Export all FAQs as a single object for easy access
export const allFAQs = {
  homepage: homepageFAQs,
  webDevelopment: webDevelopmentFAQs,
  uiUxDesign: uiUxDesignFAQs,
  mobileAppDevelopment: mobileAppDevelopmentFAQs,
  aiIntegration: aiIntegrationFAQs,
  aiConsulting: aiConsultingFAQs,
  cloudDevOps: cloudDevOpsFAQs,
  backendDevelopment: backendDevelopmentFAQs,
  workflowAutomation: workflowAutomationFAQs,
  n8nAutomation: n8nAutomationFAQs,
  zapierIntegration: zapierIntegrationFAQs,
  makeAutomation: makeAutomationFAQs,
  marketingServices: marketingServicesFAQs,
  whatsappAutomation: whatsappAutomationFAQs,
  rcsMessaging: rcsMessagingFAQs,
  linkedInOutreach: linkedInOutreachFAQs,
  emailMarketing: emailMarketingFAQs,
  bulkAutomationMarketing: bulkAutomationMarketingFAQs,
  aimlServices: aimlServicesFAQs,
  machineLearning: machineLearningFAQs,
  deepLearning: deepLearningFAQs,
  reinforcementLearning: reinforcementLearningFAQs,
  naturalLanguageProcessing: naturalLanguageProcessingFAQs,
  computerVision: computerVisionFAQs,
  predictiveAnalytics: predictiveAnalyticsFAQs,
  dataScience: dataScienceFAQs,
  aiChatbots: aiChatbotsFAQs,
  mailValidator: mailValidatorFAQs,
  quickSite: quickSiteFAQs,
  blog: blogFAQs,
  contact: contactFAQs,
  about: aboutFAQs
};

export default allFAQs;

